import { Pencil, ShieldOff, UserX } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { Can, useAuth } from '@/features/auth'
import { safeFormatDate } from '@/shared/utils/formatDate'
import { UserRoleBadge } from './UserRoleBadge'
import { UserStatusBadge } from './UserStatusBadge'
import type { OrgMember } from '../api/operations'

interface UsersTableProps {
  /**
   * Miembros CON rol (goal portal-correcciones-1): el backend ya no devuelve a
   * los de la organización sin rol, así que la tabla no tiene ramas para ese
   * caso — todas las filas se pueden cambiar y quitar de rol.
   */
  members: OrgMember[]
  onChangeRole: (member: OrgMember) => void
  /**
   * Quitar el rol (goal portal-roles-2): conserva la membresía, cierra los
   * datos y —desde portal-correcciones-1— saca al miembro de este listado.
   */
  onRemoveRole: (member: OrgMember) => void
  onDeactivate: (member: OrgMember) => void
}

function memberFullName(m: OrgMember): string {
  return [m.firstName, m.lastName].filter(Boolean).join(' ').trim() || m.email
}

function memberInitials(m: OrgMember): string {
  const first = m.firstName?.[0] ?? m.email[0] ?? '?'
  const last = m.lastName?.[0] ?? ''
  return (first + last).toUpperCase()
}

export function UsersTable({
  members,
  onChangeRole,
  onRemoveRole,
  onDeactivate,
}: UsersTableProps) {
  const { user } = useAuth()
  const meId = user?.id

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-ink-subtle">
            <th className="px-2 py-3">Usuario</th>
            <th className="px-2 py-3">Rol</th>
            <th className="px-2 py-3">Estado</th>
            <th className="hidden px-2 py-3 md:table-cell">Registrado</th>
            <th className="px-2 py-3 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => {
            const isMe = meId === member.id
            const isOwner = member.role === 'ORG_OWNER'

            return (
              <tr key={member.id} className="border-b border-line last:border-b-0">
                <td className="px-2 py-3">
                  <div className="flex items-center gap-3">
                    {member.photoSrc ? (
                      <img
                        src={member.photoSrc}
                        alt={memberFullName(member)}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-amber-500 to-red-500 text-[12px] font-bold text-white">
                        {memberInitials(member)}
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="truncate font-medium text-ink">
                        {memberFullName(member)}
                        {isMe && (
                          <span className="ml-2 text-xs font-normal text-ink-muted">(tú)</span>
                        )}
                      </p>
                      <p className="truncate text-xs text-ink-muted">{member.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-2 py-3">
                  <UserRoleBadge roleName={member.role} />
                </td>
                <td className="px-2 py-3">
                  <UserStatusBadge isActive={member.isActive} />
                </td>
                <td className="hidden px-2 py-3 text-ink-muted md:table-cell">
                  {safeFormatDate(member.registeredAt, '—')}
                </td>
                <td className="px-2 py-3 text-right">
                  {isMe ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0} className="text-xs text-ink-subtle">
                          —
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>No puedes modificar tu propio acceso</TooltipContent>
                    </Tooltip>
                  ) : isOwner ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0} className="text-xs text-ink-subtle">
                          —
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        El rol de Dueño no se puede modificar desde aquí por ahora
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <div className="inline-flex items-center gap-1">
                      <Can permission="users:update_role">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onChangeRole(member)}
                          aria-label={`Cambiar rol de ${memberFullName(member)}`}
                        >
                          <Pencil className="h-4 w-4" />
                          <span className="hidden md:inline">Cambiar rol</span>
                        </Button>
                      </Can>
                      <Can permission="users:update_role">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onRemoveRole(member)}
                          aria-label={`Quitar rol a ${memberFullName(member)}`}
                        >
                          <ShieldOff className="h-4 w-4" />
                          <span className="hidden md:inline">Quitar rol</span>
                        </Button>
                      </Can>
                      {member.isActive && (
                        <Can permission="users:deactivate">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onDeactivate(member)}
                            aria-label={`Desactivar a ${memberFullName(member)}`}
                            className="text-danger hover:text-danger"
                          >
                            <UserX className="h-4 w-4" />
                            <span className="hidden md:inline">Desactivar</span>
                          </Button>
                        </Can>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
